<template>
  <div class="button-container">
    <transition name="fade">
      <Overlay v-if="paymentPageOpen" message="Please close the subscriptions popup to continue."/>
    </transition>
    <button v-if="!text && !viewAllPlans" :class="['subscribe-button', small ? 'small' : 'normal', leftAlign ? 'left-align' : '']" @click="buttonHandler">{{ buttonText }}</button>
    <p v-if="text" class="red pointer text-button" @click="spawnStripeWindow">Subscription settings</p>
    <button v-if="viewAllPlans" class="primaryButton plans-button" @click="spawnStripeWindow">View all Plans</button>
  </div>
</template>

<script>
import { BrowserWindow } from 'electron';
import * as remote from '@electron/remote';

import Overlay from "@/components/Overlay";

import UpdatedUser from "@/subscriptions/UpdatedUser";
import GetSubscription from "@/queries/GetSubscription";

import { Auth } from "aws-amplify";

import {
  trackEvent,
  trackScreenView,
  EVENT_CATEGORIES,
  SUBSCRIPTION_ACTIONS
} from "@/Analytics";
const SUBSCRIPTION_EVENT = EVENT_CATEGORIES.SUBSCRIPTION_EVENT;

export default {
  name: "subscribe-button",
  components: {
    Overlay
  },
  props: {
    small: {
      type: Boolean,
      required: false,
      default: false
    },
    text: {
      type: Boolean,
      required: false,
      default: false
    },
    viewAllPlans: {
      type: Boolean,
      required: false,
      default: false
    },
    leftAlign: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      user: '',
      paymentPageOpen: false
    };
  },
  apollo: {
    $subscribe: {
      updatedUser: {
        query: UpdatedUser,
        variables () {
          return {
            id: this.$store.getters.userId
          };
        },
        result({ data }) {
          // console.log('subscription result!');
          // console.log(data.updatedUser);
          let viewCap = null;
          if (data.updatedUser.type === 'artist') {
            viewCap = this.$store.getters.planSettings.artistViewLimit;
          } else if (data.updatedUser.type === 'business' || data.updatedUser.type === 'virtuoso') {
            viewCap = this.$store.getters.planSettings.virtuosoViewLimit;
          } else if (data.updatedUser.type === 'free') {
            viewCap = this.$store.getters.planSettings.freeViewLimit;
          }

          if (viewCap !== null) {
            console.log('SETTING VIEW CAP SUB BUTTON', viewCap);
            this.$store.commit("SET_VIEW_CAP", {
              viewCap: viewCap
            });
          }

          if (data.updatedUser.viewCount) {
            this.$store.commit("SET_VIEW_COUNT", {
              viewCount: data.updatedUser.viewCount
            });
          }

          if (data.updatedUser.type) {
            this.$store.commit("SET_ARTWORK_STORE_UPDATE_REQUIRED", {
              artworkStoreUpdateRequired: true
            });
          }

          this.refetchSubscriptionData();
        }
      }
    },
    getSubscription: {
      query: GetSubscription,
      fetchPolicy: "no-cache",
      variables () {
        return {
          subscription_id: this.$store.getters.userDetails.subscriptionid
        }
      },
      result({ data, loading }) {
        // Guard against undefined data (e.g., during logout when store is cleared)
        if (!data || !data.getSubscription || !data.getSubscription.subscriptionItems) {
          console.warn('[SubscribeButton] No subscription data available');
          return;
        }
        
        let group;

        const monthlyPlanItem = data.getSubscription.subscriptionItems.find(item => item.plan?.nickname?.includes('Monthly'));
        const monthlyPlan = monthlyPlanItem?.plan;
        
        if (!monthlyPlan) {
          console.warn('[SubscribeButton] No monthly plan found in subscription');
          return;
        }
        
        const stripeMetadata = JSON.parse(monthlyPlan.metadata || '{}');

        if (monthlyPlan.nickname?.includes("Virtuoso")) group = "virtuoso";
        if (monthlyPlan.nickname?.includes("Business")) group = "business";
        if (monthlyPlan.nickname?.includes("Artist")) group = "artist";
        if (monthlyPlan.nickname?.includes("Free")) group = "free";

        this.$store.commit("SET_GROUP", {
          group: group
        });

        this.$store.commit("SET_SUBSCRIPTION", {
          subscription: data.getSubscription
        });

        this.$store.commit("SET_MAX_ANIMATION_DURATION", {
          maxAnimationDuration: parseInt(stripeMetadata.duration, 10)
        });

        this.$store.commit('PanelsApp/SET_MAX_ASSET_SIZE', {
          maxAssetSize: stripeMetadata.panelsSize
        });
      },
      error(error) {
        // Handle errors gracefully (e.g., store reset during logout)
        console.warn('[SubscribeButton] Subscription query error:', error?.message || error);
      },
      skip() {
        // return !this.$store.getters.userDetails.subscriptionid || this.$store.getters.subscriptionDetails.canceled_at;
        return !this.$store.getters.userDetails.subscriptionid;
      }
    }
  },
  computed: {
    buttonText() {
      return this.$store.getters.userDetails.group === "free" ?
        "Upgrade Plan" :
        "View Plan"
    }
  },
  methods: {
    buttonHandler() {
      this.$router.push({ name: "subscriptions-page" }).catch(() => {});
      // if (this.$store.getters.userDetails.group === "free") {
      //   this.spawnStripeWindow();
      // } else {
      //   this.$router.push({ name: "plan-page" });
      // }
    },
    spawnStripeWindow() {
      trackEvent(SUBSCRIPTION_EVENT, SUBSCRIPTION_ACTIONS.SUBSCRIPTIONS_WINDOW_OPENED);

      // if (!this.paymentPageOpen) return;

      this.paymentPageOpen = true;

      console.warn('Trying to show payment page.');

      // Check if we're in browser mode (no ipcRenderer)
      const userDataPath = remote.app.getPath("userData");
      if (userDataPath.startsWith('browser://')) {
        // Browser mode - open payment page in new window
        const isDevelopment = process.env.VUE_APP_ENDPOINTS !== "production";
        const baseUrl = isDevelopment ? 'http://localhost:9090' : 'https://payments.eyejackapp.com';
        const paymentWindow = window.open(baseUrl, '_blank', 'width=600,height=700');
        
        // Poll for when the window is closed
        const checkClosed = setInterval(() => {
          if (paymentWindow && paymentWindow.closed) {
            clearInterval(checkClosed);
            this.paymentPageOpen = false;
            this.refetchSubscriptionData();
          }
        }, 500);
      } else {
        // Electron mode - use ipcRenderer
        const { ipcRenderer } = require('electron');
        ipcRenderer.send('show_payment_page', { queryParams: '' });
        ipcRenderer.once('payment_page_hidden', () => {
          this.paymentPageOpen = false;
          this.refetchSubscriptionData();
        });
      }
    },
    refetchSubscriptionData() {
      if (this._isBeingDestroyed || this._isDestroyed) return;
      if (!this.$store.getters.userId) return;
      this.$apollo.queries.getSubscription?.refetch()?.catch(err => {
        console.warn('[SubscribeButton] Refetch failed (likely during logout):', err?.message);
      });
    }
  }
};
</script>

<style lang="stylus" scoped>
@import '../styles/main.styl'

.button-container {
  margin-top: 0px !important
}

.subscribe-button {
  font-weight: normal;
  background: linear-gradient(#FFEB6F, #F6D619, #FFEB6F);
  box-shadow: rgba(0, 0, 0, 0.25) 1px 1px 2px 0;
  transition: transform 0.1s;
  position: relative;

  &:hover {
    cursor: pointer;
    transform: scale(1.05);
  }
}

.small {
  width: 110px;
  height: 36px;
  border-radius: 5px;
  border: 2px solid #F6D619;
  font-size: 1.3em;
}

.normal {
  width: 150px;
  height: 46px;
  border-radius: 8px;
  border: 3px solid #F6D619;
  font-size: 2em;
  // color: rgba(255, 255, 255, 0.7);
  padding: 1px 1px 0 0;

  // &::after {
  //   content: "Subscribe";
  //   color: color-heading;
  //   position: absolute;
  //   left: 21px;
  //   top: 8px;
  // }
}

.text-button {
  margin: 24px 0 0 0;
  width: 100%;
}

.left-align {
  margin-right: auto;
}

.plans-button {
  margin: 0 auto;
  font-size: 14px;
}
</style>
